import { ReactElement, useMemo } from 'react';

import LanguageIcon from '@mui/icons-material/Language';

import Org from '../../types/Org';
import GratiCard, { GratiCardAction, GratiCardSlotClickHandlers } from '../common/GratiCard';

import { OrgPreviewElement } from './orgPreviewTypes';

interface OrgCardPreviewProps {
  org: Org;
  variant?: 'elevation' | 'outlined' | 'none';
  /** Called when a card element is clicked (for interactive preview) */
  onElementClick?: (element: OrgPreviewElement) => void;
}

/**
 * A preview version of OrgCard that doesn't depend on Firestore hooks.
 * Used for previewing organizations before they are saved.
 *
 * Key differences from OrgCard:
 * - No data hooks (useData, useNavigate, etc.)
 * - No TopActions menu (no edit button)
 * - Reactive to org prop changes for real-time preview updates
 * - Supports interactive mode via onElementClick for edit preview
 */
export default function OrgCardPreview({
  org,
  variant = 'outlined',
  onElementClick,
}: OrgCardPreviewProps): ReactElement {
  // Build slot click handlers for interactive preview mode
  const slotClickHandlers: GratiCardSlotClickHandlers | undefined = useMemo(() => {
    if (!onElementClick) return undefined;

    return {
      avatar: () => onElementClick('logo'),
      title: () => onElementClick('name'),
      category: () => onElementClick('description'),
      state: () => onElementClick('website'),
    };
  }, [onElementClick]);

  // Render website button in state slot when available
  const stateAction: GratiCardAction | undefined = useMemo(() => {
    if (org.homeUrl) {
      return {
        text: 'Website',
        icon: <LanguageIcon />,
        actionHandler: () => {
          // In preview mode, clicking navigates to the field; otherwise opens URL
          if (onElementClick) {
            onElementClick('website');
          } else {
            window.open(org.homeUrl, '_blank', 'noopener,noreferrer');
          }
        },
      };
    }
    return undefined;
  }, [org.homeUrl, onElementClick]);

  return (
    <GratiCard
      key={org.itemId || 'preview'}
      variant={variant}
      avatar={org.logo}
      avatarStorageLocation={
        org.itemId
          ? `org/${org.itemId}/publicFile`
          : undefined
      }
      category={org.description}
      title={org.name}
      state={stateAction}
      slotClickHandlers={slotClickHandlers}
    />
  );
}
